import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col px-3 py-4 space-y-2" }
const _hoisted_2 = { class: "flex flex-row space-x-2" }

import { computed, onMounted, PropType, ref, watch } from 'vue';
import { IOption, ManagementDestinationOption, ManagementStepField } from '@/lib';
import { ManagementPlanStepSelected } from '@/models';
import { BaseSelect } from '@/lib/components/Select';
import { BaseIcon } from '@/lib/components/Icon';
import { BaseButton } from '@/lib/components/Button';
import { BaseTextInput } from '@/lib/components/Input';
import { arrayUnique } from '@/helpers/array.helper';


export default /*#__PURE__*/_defineComponent({
  __name: 'ManagementPlanStep',
  props: {
  value: {
    type: Array as PropType<ManagementStepField[]>,
    default: () => [],
    validator: (value: ManagementStepField[]) => value.length >= 3
  },
  destinations: {
    type: Array as PropType<ManagementDestinationOption[]>,
    default: () => []
  },
  timeframes: {
    type: Array as PropType<IOption[]>,
    default: () => []
  },
  selectedCombinations: {
    type: Array as PropType<ManagementPlanStepSelected[]>,
    default: () => []
  },
  stepIndex: {
    type: Number,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  }
},
  emits: ["addStep", "updateStep", "removeStep", "showError"],
  setup(__props, { emit: __emit }) {

const props = __props;
const emit = __emit;

const updatedStep = ref<ManagementPlanStepSelected>({
  destination_id: '',
  timeframe_id: '',
  notes: '',
  step_id: ''
});
const destinationId = computed<string>(() => props.value[0].value);
const timeframeId = computed<string>(() => props.value[1].value);
const notes = computed<string>(() => props.value[2].value);
const selectedDestination = computed<ManagementDestinationOption | null>(() =>
  props.destinations?.find((option) => option.value === updatedStep.value.destination_id) ?? null
);
const isTimeframeFieldDisabled = computed<boolean>(() =>
  !!selectedDestination.value && !selectedDestination.value.require_timeframe
);
const noneTimeframeOptionId = computed<string>(() => props.timeframes.find((option) => option.label === 'None')?.value ?? '');
const destinationOptions = computed<ManagementDestinationOption[]>(() => {
  // When a user can choose or change timeframe, destination list should be updated too.
  const otherSelectedDestinationId = props.selectedCombinations
    .filter((selected: ManagementPlanStepSelected) =>
      selected.destination_id !== updatedStep.value.destination_id && selected.timeframe_id === updatedStep.value.timeframe_id
    )
    .map((selected: ManagementPlanStepSelected) => selected.destination_id);

  const noTimeframeDestinationIds = props.destinations
    .filter((destination: ManagementDestinationOption) => !destination.require_timeframe)
    .map((destination: ManagementDestinationOption) => destination.value);
  const selectedDestinationIds = arrayUnique(
    props.selectedCombinations.map((selected: ManagementPlanStepSelected) => selected.destination_id)
  );

  const intersectionIds = noTimeframeDestinationIds.filter((item) => selectedDestinationIds.includes(item));

  const destinationFilteredOptions = props.destinations.filter(
    (item) =>
      (!intersectionIds.includes(item.value) || updatedStep.value.destination_id === item.value) &&
          !otherSelectedDestinationId.includes(item.value)
  );
  return destinationFilteredOptions.length ? destinationFilteredOptions : props.destinations;
});
const timeframeOptions = computed<IOption[]>(() => {
  const otherSelectedTimeframeId = props.selectedCombinations
    .filter(
      (selected: ManagementPlanStepSelected) =>
        selected.destination_id === updatedStep.value.destination_id &&
              selected.timeframe_id !== updatedStep.value.timeframe_id
    )
    .map((selected: ManagementPlanStepSelected) => selected.timeframe_id);
  const timeframeFilteredOptions = props.timeframes.filter((item) => !otherSelectedTimeframeId.includes(item.value));
  return timeframeFilteredOptions.length ? timeframeFilteredOptions : props.timeframes;
});
// The management step is valid if the destination id is provided and the timeframe id is not none when required by the destination
const isValid = computed<boolean>(() => updatedStep.value.destination_id && !isTimeFrameError.value);

const isSelectedDestinationOptionRequired = computed<boolean>(() => selectedDestination.value?.require_timeframe);
const isTimeFrameError = computed<boolean>(() => isSelectedDestinationOptionRequired.value && !updatedStep.value.timeframe_id);
const isDestinationError = computed<boolean>(() => updatedStep.value.timeframe_id && !updatedStep.value.destination_id);

watch([
  () => props.value,
  () => props.selectedCombinations,
  () => destinationId.value,
  () => timeframeId.value
], () => setUpdatedStep(),
{ deep: true }
);

onMounted(() => {
  setUpdatedStep();
});
const setUpdatedStep = () => {
  updatedStep.value = {
    ...updatedStep.value,
    destination_id: destinationId.value,
    timeframe_id: timeframeId.value,
    notes: notes.value,
    step_id: props.value[0].step_id ? props.value[0].step_id : updatedStep.value.step_id
  };
};
const updateOrCreateStep = () => {
  emit('showError', !isValid.value);
  if (!isValid.value) {
    return;
  }

  if (isValid.value && !props.value[0].step_id) {
    emit('addStep', updatedStep.value);
  } else if (props.value[0].step_id) {
    emit('updateStep', updatedStep.value);
  }
};
const handleUpdateDestination = () => {
  let timeframeId = null;
  if (isTimeframeFieldDisabled.value) {
    // set timeframe_id to none (option_id) when timeframeSelect is disabled.
    timeframeId = noneTimeframeOptionId.value;
  }
  updatedStep.value = {
    ...updatedStep.value,
    timeframe_id: timeframeId
  };
  updateOrCreateStep();
};
const removeStep = (stepIndex: number) => {
  emit('removeStep', stepIndex);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_unref(BaseSelect), {
        modelValue: updatedStep.value.destination_id,
        "onUpdate:modelValue": [
          _cache[0] || (_cache[0] = ($event: any) => ((updatedStep.value.destination_id) = $event)),
          handleUpdateDestination
        ],
        options: destinationOptions.value,
        "data-dusk": "management-plan-step-destination",
        "line-height": 1.25,
        placeholder: _ctx.$t('custom.uhb.management.destination'),
        error: isDestinationError.value ? _ctx.$t('platform.error.field-required') : '',
        disabled: __props.loading,
        class: "flex-1 w-96"
      }, null, 8, ["modelValue", "options", "placeholder", "error", "disabled"]),
      _createVNode(_unref(BaseSelect), {
        modelValue: updatedStep.value.timeframe_id,
        "onUpdate:modelValue": [
          _cache[1] || (_cache[1] = ($event: any) => ((updatedStep.value.timeframe_id) = $event)),
          updateOrCreateStep
        ],
        error: isTimeFrameError.value ? _ctx.$t('platform.error.field-required') : '',
        "data-dusk": "management-plan-step-timeframe",
        options: timeframeOptions.value,
        placeholder: _ctx.$t('custom.uhb.management.timeframe'),
        class: "w-48",
        disabled: isTimeframeFieldDisabled.value || __props.loading
      }, null, 8, ["modelValue", "error", "options", "placeholder", "disabled"]),
      _createVNode(_unref(BaseButton), {
        class: "w-12 h-12",
        color: "ghost",
        onClick: _cache[2] || (_cache[2] = ($event: any) => (removeStep(__props.stepIndex)))
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(BaseIcon), {
            class: "text-base",
            name: "delete"
          })
        ]),
        _: 1
      })
    ]),
    _createVNode(_unref(BaseTextInput), {
      modelValue: updatedStep.value.notes,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((updatedStep.value.notes) = $event)),
      "data-dusk": "management-plan-step-note",
      class: "flex",
      "full-height": "",
      placeholder: _ctx.$t('custom.uhb.management.enter-notes'),
      disabled: !__props.value[0].step_id || __props.loading,
      onChange: updateOrCreateStep
    }, null, 8, ["modelValue", "placeholder", "disabled"])
  ]))
}
}

})